import React, { useState, useCallback } from 'react';
import { Calendar, Clock, Handshake, MapPin } from 'lucide-react';
import { MobileLocationInputs } from './MobileLocationInputs';
import { HapticButton } from '@/components/ui/haptic-button';
import { EnhancedButton } from '@/components/ui/enhanced-button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PullToRefresh } from '@/components/ui/pull-to-refresh';
import { RideType, RideFormData } from '@/types/ride';
import RideTypeSelector from '@/components/ride/RideTypeSelector';
import SchedulePicker from '@/components/ride/SchedulePicker';
import MapPreview from '@/components/MapPreview';
import { sameEndpoints, type RouteEndpoints } from '@/components/ride/routeEndpoints';
import { getPriceEstimate } from '@parleyride/domain';
import { RouteData } from '@/hooks/map/useRouting';
import { useToast } from '@/components/ui/use-toast';

interface MobileRideFormProps {
  onSubmit: (data: RideFormData) => void;
}

/**
 * The mobile-web twin of EnhancedRideForm, and collapsed for the same reasons —
 * see the note there. This one was three tabs (Location / When / Vehicle) with
 * a Continue between them, on the surface where the tap cost is highest.
 */
export const MobileRideForm: React.FC<MobileRideFormProps> = ({ onSubmit }) => {
  const [pickupLocation, setPickupLocation] = useState('');
  const [destination, setDestination] = useState('');
  const [scheduleMode, setScheduleMode] = useState<'now' | 'schedule'>('now');
  const [rideDate, setRideDate] = useState<Date | undefined>(new Date());
  const [rideType, setRideType] = useState<RideType>('economy');
  const [routeData, setRouteData] = useState<RouteData | null>(null);
  // Only set once both endpoints are chosen — see MobileLocationInputs.
  const [endpoints, setEndpoints] = useState<RouteEndpoints | null>(null);

  // See EnhancedRideForm: derived once so the vehicle step and the submitted
  // estimate cannot quote different numbers for the same trip.
  const routeKm = routeData ? Math.ceil(routeData.distance / 1000) : null;
  const routeMin = routeData ? Math.ceil(routeData.duration / 60) : null;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Stable identity: passed to MapPreview's onRouteCalculated, which lives in
  // its route-processing effect deps. An inline handler would re-run that effect
  // every render and flood the geocoding edge function.
  // Stable identity: it sits in MobileLocationInputs' effect deps.
  const handleEndpointsChange = useCallback((next: RouteEndpoints | null) => {
    setEndpoints((prev) => (sameEndpoints(prev, next) ? prev : next));
  }, []);

  const handleRouteCalculated = useCallback((calculatedRoute: RouteData) => {
    setRouteData(calculatedRoute);
  }, []);

  const canRequest = Boolean(pickupLocation && destination);

  const handleSubmit = async () => {
    if (!pickupLocation || !destination) {
      toast({
        title: "Missing Information",
        description: "Please enter both pickup and destination locations",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const priceEstimate = getPriceEstimate(rideType, routeKm, routeMin);
      
      const rideData: RideFormData = {
        pickupLocation,
        dropoffLocation: destination,
        destination,
        rideType,
        date: new Date(),
        isScheduled: scheduleMode === 'schedule',
        scheduledTime: scheduleMode === 'schedule' ? rideDate : undefined,
        estimatedPrice: priceEstimate.amount,
        estimatedTime: priceEstimate.time,
      };
      
      onSubmit(rideData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to process ride request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefresh = async () => {
    if (pickupLocation && destination && routeData) {
      // Re-calculate route data by resetting and letting MapPreview recalculate
      setRouteData(null);
    }
    toast({
      title: "Refreshed",
      description: "Ride options updated",
    });
  };

  return (
    <PullToRefresh onRefresh={handleRefresh} className="min-h-screen bg-background">
      <div className="mobile-bottom-padding">
        <div className="px-4 py-4 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="h-5 w-5 text-primary" />
                Where to?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <MobileLocationInputs
                pickupLocation={pickupLocation}
                destination={destination}
                setPickupLocation={setPickupLocation}
                setDestination={setDestination}
                onEndpointsChange={handleEndpointsChange}
              />

              {/* Progressive disclosure: "Now" is already right for almost
                  everyone, so the date picker stays folded away. */}
              <div className="space-y-3">
                <div
                  className="inline-flex rounded-md border p-0.5"
                  role="group"
                  aria-label="When do you need this ride?"
                >
                  {(['now', 'schedule'] as const).map((mode) => (
                    <HapticButton
                      key={mode}
                      type="button"
                      size="sm"
                      hapticType="light"
                      variant={scheduleMode === mode ? 'secondary' : 'ghost'}
                      aria-pressed={scheduleMode === mode}
                      onClick={() => setScheduleMode(mode)}
                      className="gap-1.5"
                    >
                      {mode === 'now' ? (
                        <>
                          <Clock className="h-4 w-4" /> Now
                        </>
                      ) : (
                        <>
                          <Calendar className="h-4 w-4" /> Schedule
                        </>
                      )}
                    </HapticButton>
                  ))}
                </div>

                {scheduleMode === 'schedule' && (
                  <div className="animate-fade-in">
                    <SchedulePicker rideDate={rideDate} setRideDate={setRideDate} />
                  </div>
                )}
              </div>

              {/* Map and fares arrive together, once the route is real. */}
              {endpoints ? (
                <div className="animate-fade-in space-y-4">
                  <MapPreview
                    from={endpoints.from}
                    to={endpoints.to}
                    fromCoords={endpoints.fromCoords}
                    toCoords={endpoints.toCoords}
                    className="h-56 rounded-xl"
                    showEta={true}
                    onRouteCalculated={handleRouteCalculated}
                  />
                  <RideTypeSelector
                    rideType={rideType}
                    setRideType={setRideType}
                    distanceKm={routeKm}
                    durationMin={routeMin}
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Choose a pickup and a destination to see the route and fares.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* One primary action, thumb-reachable. */}
        <div className="mobile-fixed-bottom bg-background/95 backdrop-blur-sm border-t border-muted/20">
          <div className="px-4 py-4">
            <EnhancedButton
              onClick={handleSubmit}
              disabled={!canRequest || isSubmitting}
              loading={isSubmitting}
              loadingText="Finding drivers…"
              icon={<Handshake className="h-4 w-4" />}
              className="w-full bg-gradient-to-r from-primary to-secondary text-white h-14 text-base font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Request &amp; negotiate
            </EnhancedButton>
          </div>
        </div>
      </div>
    </PullToRefresh>
  );
};

